import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { AlertTriangle, Clock, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds

// Simple ping sound using Web Audio API
const playPingSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880; // A5 note - pleasant ping
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (e) {
    console.log('Audio not supported');
  }
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lyca-renewal-tracker/artifacts/wh5xa0xk_logo_new1.webp";

export default function BigScreenDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const hasPlayedSound = useRef(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
      setLastRefresh(new Date());
      
      // Play ping sound if should_alert is true (always enabled on big screen)
      if (response.data.should_alert) {
        playPingSound();
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every hour
    const interval = setInterval(() => {
      hasPlayedSound.current = false; // Reset so sound can play again
      fetchStats();
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-[#020617] flex items-center justify-center">
        <div className="text-[#00D26A] text-xl">Loading...</div>
      </div>
    );
  }

  // Row 1: Urgent items (Overdue, Due Today, Due Tomorrow)
  const row1Cards = [
    { 
      key: "overdue", 
      label: "OVERDUE", 
      value: stats?.overdue || 0, 
      color: "text-red-500", 
      borderColor: "border-red-500/50",
      bgColor: "bg-red-500/10",
      icon: AlertTriangle 
    },
    { 
      key: "due_today", 
      label: "DUE TODAY", 
      value: stats?.due_today || 0, 
      color: "text-red-400", 
      borderColor: "border-red-400/50",
      bgColor: "bg-red-400/10",
      icon: AlertTriangle 
    },
    { 
      key: "due_tomorrow", 
      label: "DUE TOMORROW", 
      value: (stats?.due_in_1_day || 0) - (stats?.due_today || 0), 
      color: "text-orange-500", 
      borderColor: "border-orange-500/50",
      bgColor: "bg-orange-500/10",
      icon: Clock 
    },
  ];

  // Row 2: Upcoming items (2 days, 3 days, 5 days)
  const row2Cards = [
    { 
      key: "due_in_2_days", 
      label: "DUE IN 2 DAYS", 
      value: stats?.due_in_2_days || 0, 
      color: "text-yellow-500", 
      borderColor: "border-yellow-500/50",
      bgColor: "bg-yellow-500/10",
      icon: Clock 
    },
    { 
      key: "due_in_3_days", 
      label: "DUE IN 3 DAYS", 
      value: stats?.due_in_3_days || 0, 
      color: "text-blue-400", 
      borderColor: "border-blue-400/50",
      bgColor: "bg-blue-400/10",
      icon: CalendarDays 
    },
    { 
      key: "due_in_5_days", 
      label: "DUE IN 5 DAYS", 
      value: stats?.due_in_5_days || 0, 
      color: "text-slate-400", 
      borderColor: "border-slate-400/50",
      bgColor: "bg-slate-400/10",
      icon: CalendarDays 
    },
  ];

  const renderCard = (stat) => (
    <div
      key={stat.key}
      className={`rounded-lg border ${stat.borderColor} ${stat.bgColor} p-4 flex flex-col items-center justify-center`}
      data-testid={`bigscreen-stat-${stat.key}`}
    >
      <stat.icon className={`w-6 h-6 ${stat.color} mb-2`} />
      <div 
        className={`text-5xl font-bold ${stat.color} leading-none`}
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {stat.value}
      </div>
      <div 
        className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-2 text-center"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {stat.label}
      </div>
    </div>
  );

  return (
    <div 
      className="h-screen w-screen bg-[#020617] flex flex-col overflow-hidden"
      data-testid="bigscreen-dashboard"
    >
      {/* Header - Compact */}
      <header className="flex items-center justify-between px-8 py-3 border-b border-slate-800 flex-shrink-0">
        <img 
          src={LOGO_URL} 
          alt="Lyca Mobile" 
          className="h-8"
          data-testid="bigscreen-logo"
        />
        <div className="text-right">
          <h1 
            className="text-2xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Renewal Dashboard
          </h1>
          <p className="text-slate-500 text-sm">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
      </header>

      {/* Alert Banner - Compact */}
      {stats?.should_alert && (
        <div 
          className="mx-8 mt-3 px-4 py-2 rounded-lg border border-red-500/50 bg-red-500/10 flex items-center gap-3 flex-shrink-0" 
          data-testid="bigscreen-alert"
        >
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="text-sm">
            <span className="font-bold text-red-400">ATTENTION: </span>
            <span className="text-white">
              {stats.overdue > 0 && `${stats.overdue} overdue. `}
              {stats.due_today > 0 && `${stats.due_today} due today. `}
              {stats.is_friday && stats.weekend_renewals > 0 && `${stats.weekend_renewals} weekend renewals.`}
            </span>
          </div>
        </div>
      )}

      {/* Stats Grid - Two Rows */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 gap-4">
        {/* Row 1: Urgent */}
        <div 
          className="grid grid-cols-3 gap-5 w-full max-w-4xl" 
          data-testid="bigscreen-stats-row1"
        >
          {row1Cards.map(renderCard)}
        </div>
        
        {/* Row 2: Upcoming */}
        <div 
          className="grid grid-cols-3 gap-5 w-full max-w-4xl" 
          data-testid="bigscreen-stats-row2"
        >
          {row2Cards.map(renderCard)}
        </div>
      </main>

      {/* Footer - Compact */}
      <footer className="px-8 py-2 border-t border-slate-800 flex items-center justify-between flex-shrink-0">
        <div className="text-slate-600 text-xs">
          Lyca Mobile Ireland • Renewal Tracker
        </div>
        <div className="text-slate-600 text-xs">
          Auto-refresh: Every hour • {stats?.total_pending_tasks || 0} total pending
        </div>
      </footer>
    </div>
  );
}
